import React, { Dispatch, SetStateAction } from 'react';
import { Label } from '@/components/ui/label';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import { useGlobalCustomInputs } from '../hooks/useGlobalCustomInputs';
import type { CustomInput, EventFormData } from '../../drawers/types/EventPaymentTypes';
import type { Merchant } from '../../../types/common';

// Props interface for the CustomInputsInformation_Event component
interface CustomInputsInformation_EventProps {
  formData: EventFormData;
  setFormData: Dispatch<SetStateAction<EventFormData>>;
  merchant: Merchant | null;
  isEditMode: boolean;
  handleFieldFocus: (fieldName: string) => void;
  invalidFields: Set<string>;
}

export default function CustomInputsInformation_Event({
  formData,
  setFormData,
  merchant,
  isEditMode,
  handleFieldFocus,
  invalidFields,
}: CustomInputsInformation_EventProps): React.ReactElement {
  // Helper function to safely check if a field is invalid
  const isFieldInvalid = (fieldName: string): boolean => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      return invalidFields.has(fieldName);
    }
    return false;
  };

  const { displayedGlobalCustomInputs, getGlobalCustomInputValue, updateGlobalCustomInputValue } =
    useGlobalCustomInputs<CustomInput>({
      globalCustomInputs: formData.globalCustomInputs,
      merchantGlobalCustomInputs: merchant?.globalCustomInputs as CustomInput[] | undefined,
      isEditMode,
      setGlobalCustomInputs: (updater) => {
        setFormData((prevData) => ({
          ...prevData,
          globalCustomInputs: updater(prevData.globalCustomInputs ?? []),
        }));
      },
    });

  // Handle purchaser custom input changes (at formData level, displayed in custom_input_information section)
  const handlePurchaserCustomInputChange = (inputIndex: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      purchaserCustomInputs: (prev.purchaserCustomInputs ?? []).map((input, iIdx) =>
        iIdx === inputIndex
          ? {
              ...input,
              value: value,
            }
          : input,
      ),
    }));
  };

  return (
    <div>
      {/* Global Custom Inputs Section */}
      <div className="mt-2 space-y-3">
        {displayedGlobalCustomInputs.map((input: CustomInput, inputIndex: number) => {
          const inputValue = getGlobalCustomInputValue(input, inputIndex);

          return (
            <div key={inputIndex} className="space-y-0">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                {input.title?.trim?.()}
                {input.required && <span className="ml-1 text-red-500">*</span>}
                {!input.required && <span className="ml-1 text-ink-muted">(optional)</span>}
              </Label>

              {input.type === 'SINGLE_SELECTION_DROPDOWN' && (
                <select
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                      ${
                        isFieldInvalid(`global-custom-${inputIndex}`)
                          ? 'border-red-500'
                          : 'border-edge bg-white'
                      }
                  `}
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                >
                  <option value="">Select an option</option>
                  {input.options?.map((option: string, optIdx: number) => (
                    <option key={optIdx} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}

              {input.type === 'SHORT_TEXT_INPUT' && (
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                          ${
                            isFieldInvalid(`global-custom-${inputIndex}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          }
                      `}
                  placeholder="Enter your answer"
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                />
              )}

              {input.type === 'LONG_TEXT_INPUT' && (
                <textarea
                  value={inputValue}
                  onChange={(e) => {
                    updateGlobalCustomInputValue(inputIndex, input, e.target.value);
                  }}
                  className={`
                          block min-h-[100px] w-full shrink-0 rounded-md border px-2 py-1.5 text-sm leading-tight
                          focus:border-sage focus:outline-none focus:ring-sage
                          ${
                            isFieldInvalid(`global-custom-${inputIndex}`)
                              ? 'border-red-500'
                              : 'border-edge bg-white'
                          }
                      `}
                  rows={4}
                  placeholder="Enter your answer"
                  required={input.required}
                  onFocus={() => handleFieldFocus(`global-custom-${inputIndex}`)}
                />
              )}

              {input.type === 'MULTI_SELECTION_DROPDOWN' && (
                <MultiSelectDropdownNoCreate
                  height={28}
                  options={
                    input.options?.map((option: string) => ({
                      value: option,
                      label: option,
                    })) || []
                  }
                  items={inputValue
                    .split(',')
                    .filter((v: string) => v.trim() !== '')}
                  setItems={(selectedValues: string[]) => {
                    updateGlobalCustomInputValue(inputIndex, input, selectedValues.join(','));
                  }}
                  error={isFieldInvalid(`global-custom-${inputIndex}`)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Purchaser Custom Inputs Section (from event/ticket with questionType: PURCHASER) */}
      {formData.purchaserCustomInputs && formData.purchaserCustomInputs.length > 0 && (
        <div className="mt-4 space-y-3">
          <Label className="block font-inter text-sm font-semibold leading-5 text-ink-secondary">
            Event Questions
          </Label>
          {formData.purchaserCustomInputs.map((input: CustomInput, inputIndex: number) => {
            // Normalize input type
            const inputType = input.type?.toUpperCase() || '';
            const isTextInput =
              inputType === 'TEXT' ||
              inputType === 'SHORT_ANSWER' ||
              inputType === 'SHORT_TEXT_INPUT';
            const isTextarea =
              inputType === 'TEXTAREA' ||
              inputType === 'PARAGRAPH' ||
              inputType === 'LONG_TEXT_INPUT';
            const isDropdown =
              inputType === 'DROPDOWN' ||
              inputType === 'MULTIPLE_CHOICE' ||
              inputType === 'SINGLE_SELECTION_DROPDOWN';
            const isMultiSelect = inputType === 'MULTI_SELECTION_DROPDOWN';

            return (
              <div key={inputIndex} className="space-y-0">
                <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                  {input.title?.trim?.()}
                  {input.required && <span className="ml-1 text-red-500">*</span>}
                  {!input.required && <span className="ml-1 text-ink-muted">(optional)</span>}
                </Label>

                {isDropdown && (
                  <select
                    value={formData.purchaserCustomInputs?.[inputIndex]?.value || ''}
                    onChange={(e) => handlePurchaserCustomInputChange(inputIndex, e.target.value)}
                    className={`
                              block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                              ${
                                isFieldInvalid(`purchaser-custom-${inputIndex}`)
                                  ? 'border-red-500'
                                  : 'border-edge bg-white'
                              }
                            `}
                    required={input.required}
                    onFocus={() => handleFieldFocus(`purchaser-custom-${inputIndex}`)}
                  >
                    <option value="">Select an option</option>
                    {input.options?.map((option: string, optIdx: number) => (
                      <option key={optIdx} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}

                {isTextInput && (
                  <input
                    type="text"
                    value={formData.purchaserCustomInputs?.[inputIndex]?.value || ''}
                    onChange={(e) => handlePurchaserCustomInputChange(inputIndex, e.target.value)}
                    className={`
                              block h-7 w-full shrink-0 rounded-md border px-2 py-0 text-sm leading-tight
                              ${
                                isFieldInvalid(`purchaser-custom-${inputIndex}`)
                                  ? 'border-red-500'
                                  : 'border-edge bg-white'
                              }
                            `}
                    placeholder="Enter your answer"
                    required={input.required}
                    onFocus={() => handleFieldFocus(`purchaser-custom-${inputIndex}`)}
                  />
                )}

                {isTextarea && (
                  <textarea
                    value={formData.purchaserCustomInputs?.[inputIndex]?.value || ''}
                    onChange={(e) => handlePurchaserCustomInputChange(inputIndex, e.target.value)}
                    className={`
                              block min-h-[100px] w-full shrink-0 rounded-md border px-2 py-1.5 text-sm leading-tight
                              focus:border-sage focus:outline-none focus:ring-sage
                              ${
                                isFieldInvalid(`purchaser-custom-${inputIndex}`)
                                  ? 'border-red-500'
                                  : 'border-edge bg-white'
                              }
                            `}
                    rows={4}
                    placeholder="Enter your answer"
                    required={input.required}
                    onFocus={() => handleFieldFocus(`purchaser-custom-${inputIndex}`)}
                  />
                )}

                {isMultiSelect && (
                  <MultiSelectDropdownNoCreate
                    height={28}
                    options={
                      input.options?.map((option: string) => ({
                        value: option,
                        label: option,
                      })) || []
                    }
                    items={(formData.purchaserCustomInputs?.[inputIndex]?.value || '')
                      .split(',')
                      .filter((v: string) => v.trim() !== '')}
                    setItems={(selectedValues: string[]) => {
                      handlePurchaserCustomInputChange(inputIndex, selectedValues.join(','));
                    }}
                    error={isFieldInvalid(`purchaser-custom-${inputIndex}`)}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
